import { useEffect, useState } from 'react';
import type { Lang } from '../lib/i18n';
import { TOPIC_IDS, showingText, topicLabel, topicUi } from '../lib/blogTopics';
import '../styles/topic-filter.css';

type Categorized = { category: string };

/**
 * Selected blog topic, mirrored in `?topic=<id>` so a filtered view can be shared.
 * The pre-rendered page always lists every post (good for crawlers); the query is
 * applied after hydration.
 */
export function useTopicFilter<P extends Categorized>(posts: P[]) {
  const [topic, setTopic] = useState('all');

  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('topic');
    if (fromUrl && posts.some((p) => p.category === fromUrl)) setTopic(fromUrl);
    // Only on first load — later changes come from select().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = (id: string) => {
    setTopic(id);
    const url = new URL(window.location.href);
    if (id === 'all') url.searchParams.delete('topic');
    else url.searchParams.set('topic', id);
    window.history.replaceState(window.history.state, '', url);
  };

  const visible = topic === 'all' ? posts : posts.filter((p) => p.category === topic);
  return { topic, select, visible };
}

/** "Topics: All 82 · Training 18 · … · More topics…" pill bar with a result count. */
export function TopicFilter({
  lang,
  posts,
  topic,
  onSelect,
}: {
  lang: Lang;
  posts: Categorized[];
  topic: string;
  onSelect: (id: string) => void;
}) {
  const ui = topicUi[lang];
  const counts = new Map<string, number>();
  for (const p of posts) counts.set(p.category, (counts.get(p.category) || 0) + 1);

  // Biggest topics first; the three largest get their own pill, the rest go under "More topics…".
  const topics = TOPIC_IDS.filter((id) => counts.get(id)).sort((a, b) => counts.get(b)! - counts.get(a)!);
  const pills = topics.length <= 4 ? topics : topics.slice(0, 3);
  const more = topics.slice(pills.length);
  const moreActive = more.includes(topic);
  const shown = topic === 'all' ? posts.length : counts.get(topic) || 0;

  const Pill = ({ id, label, count }: { id: string; label: string; count: number }) => (
    <button
      type="button"
      className={`topic-pill${topic === id ? ' is-active' : ''}`}
      aria-pressed={topic === id}
      onClick={() => onSelect(id)}
    >
      {label}
      <span className="topic-count">{count}</span>
    </button>
  );

  return (
    <div className="topic-filter">
      <div className="topic-bar" role="group" aria-label={ui.topics}>
        <span className="topic-label">{ui.topics}</span>
        <Pill id="all" label={ui.all} count={posts.length} />
        {pills.map((id) => (
          <Pill key={id} id={id} label={topicLabel(id, lang)} count={counts.get(id)!} />
        ))}
        {more.length > 0 && (
          <label className={`topic-pill topic-more${moreActive ? ' is-active' : ''}`}>
            <select
              value={moreActive ? topic : ''}
              onChange={(e) => onSelect(e.target.value || 'all')}
              aria-label={ui.more}
            >
              <option value="">{ui.more}</option>
              {more.map((id) => (
                <option key={id} value={id}>
                  {topicLabel(id, lang)} ({counts.get(id)})
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <p className="topic-showing" aria-live="polite">
        {showingText(lang, shown, posts.length)}
      </p>
    </div>
  );
}
